import * as Sentry from '@sentry/react';
import { createBrowserHistory } from '@tanstack/react-router';
import React from 'react';
import { createRoot } from 'react-dom/client';

import App from '@/app';
import { callPrefetch } from '@/lib/thoughtspot';
import { injectWalkMe } from '@/lib/walkme';
import {
  getDenyUrls,
  getIgnoreErrors,
  getReplaySessionSampleRate,
  getTracePropagationTargets,
  getTracesSampleRate,
} from '@/lib/sentryConfig';
import { createAppRouter } from './router';
import '../index.css';
import packageJson from '../package.json';

if (!import.meta.env.PROD) {
  console.info(`Dashboard v${packageJson.version} `);
}
document.documentElement.dataset.colorScheme = 'default';
document.documentElement.dataset.theme = 'light';

injectWalkMe();
callPrefetch();

const el = document.getElementById('root');

if (el) {
  const router = createAppRouter({ history: createBrowserHistory() });
  const environment = import.meta.env.VITE_SENTRY_ENV || 'local'; // See IGNITE_ENVIRONMENTS for possible values

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment,
    release: `${import.meta.env.VITE_SENTRY_DISTRIBUTION}@${environment}`,
    dist: import.meta.env.VITE_SENTRY_DISTRIBUTION,
    debug: import.meta.env.VITE_SENTRY_DEBUG === 'true',

    // Performance Monitoring
    tracesSampleRate: getTracesSampleRate(),
    tracePropagationTargets: getTracePropagationTargets(),

    // Session Replay
    replaysSessionSampleRate: getReplaySessionSampleRate(),
    replaysOnErrorSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAY_ERROR_SAMPLE_RATE || '1.0'),

    // Integrations
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: true, // Protect form inputs
      }),
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [400, 599],
      }),
      Sentry.contextLinesIntegration(),
      Sentry.linkedErrorsIntegration(),
      Sentry.extraErrorDataIntegration({ depth: 5 }),
    ],

    // Error Filtering
    ignoreErrors: getIgnoreErrors(),
    denyUrls: getDenyUrls(),

    // Breadcrumbs
    maxBreadcrumbs: 50,

    // PII Protection
    beforeSend(event) {
      // Remove sensitive headers
      if (event.request?.headers) {
        delete event.request.headers.Authorization;
        delete event.request.headers.Cookie;
      }

      return event;
    },

    // Performance Filtering
    beforeSendTransaction(event) {
      // Filter out very fast transactions (noise)
      if (event.start_timestamp && event.timestamp) {
        const duration = event.timestamp - event.start_timestamp;
        if (duration < 0.01) {
          return null;
        }
      }

      return event;
    },
  });

  // Set initial context
  Sentry.setContext('application', {
    name: 'dashboard',
    version: import.meta.env.VITE_SENTRY_DISTRIBUTION,
    environment,
  });

  const root = createRoot(el);

  root.render(
    <React.StrictMode>
      <App router={router} />
    </React.StrictMode>,
  );
} else {
  throw new Error('Could not find root element');
}
